import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
} from "react"
import * as z from "zod"
import GuestCheckoutSummary from "./GuestCheckoutSummary"
import Loader from "../ui/loader"
import Recaptcha from "../auth/register-form/Recaptcha"
import secureLocalStorage from "react-secure-storage"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { RegisterFormContext } from "../auth/register-form/context"
import { ServiceRequests } from "../../api/app.service"
import { SvgWarning, SvgLock2 } from "../icons"
import { UserCtx } from "../../context/userContext"
import { UserExists } from "../../api/auth.service"
import { useLocation } from "react-router-dom"
import { useToast } from "../../hooks/useToast"

interface Props {
  coachPublicProfileData: any
  service: any
  setEmailExists: any
  setGuestEmail: any
  cardFieldErrors: {
    cardName: boolean
    cardNumber: boolean
    cardExpiry: boolean
    cardCvv: boolean
  }
  setCardFieldErrors: React.Dispatch<
    React.SetStateAction<{
      cardName: boolean
      cardNumber: boolean
      cardExpiry: boolean
      cardCvv: boolean
    }>
  >
}

const ServiceGuestCreation: React.FC<Props> = ({
  coachPublicProfileData,
  service,
  setEmailExists,
  setGuestEmail,
  cardFieldErrors,
  setCardFieldErrors,
}) => {
  const { toast } = useToast()
  const { user } = useContext(UserCtx)
  const { createAccount } = useContext(RegisterFormContext)
  const {
    email,
    setEmail,
    setFirstName,
    firstName,
    setLastName,
    lastName,
    setPassword,
    setIsTemp,
    contactCreated,
  } = useContext(RegisterFormContext)
  const [loadingAvailablePeriods, setLoadingAvailablePeriods] =
    useState<boolean>(false)
  const [isOwner, setIsOwner] = useState<boolean>()
  const [isNotRobot, setIsNotRobot] = useState<boolean>(false)
  const [connectionCompleted, setConnectionCompleted] = useState<boolean>(false)
  const [discountedOrderTotal, setDiscountedOrderTotal] = useState(0)
  const [leftHeight, setLeftHeight] = useState<number | null>(null)
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [validatingForm, setValidatingForm] = useState<boolean>(false)
  const [spotsLeft, setSpotsLeft] = useState<number | null>(null)
  const [errors, setErrors] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    confUserPassword: "",
  })
  const location = useLocation()
  const pathParts = location.pathname.split("/").filter(Boolean)
  const guestCheckout =
    discountedOrderTotal !== 0 &&
    pathParts.length === 3 &&
    pathParts[1] === "checkout"
  const fullEnrollment = spotsLeft !== null && spotsLeft <= 0

  useLayoutEffect(() => {
    if (leftContainerRef.current) {
      setLeftHeight(leftContainerRef.current.offsetHeight)
    }
  }, [loadingAvailablePeriods])

  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  const leftContainerRef = useRef<HTMLDivElement | null>(null)

  const generatePassword = (length: number = 12): string => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
    const lower = "abcdefghijklmnopqrstuvwxyz"
    const digits = "0123456789"
    const specials = "!@#$%^&*()_+-=[]{}|;:,.<>?"
    const all = upper + lower + digits + specials

    const getRandom = (str: string) =>
      str.charAt(Math.floor(Math.random() * str.length))

    let password = [
      getRandom(upper),
      getRandom(lower),
      getRandom(digits),
      getRandom(specials),
    ]

    for (let i = password.length; i < length; i++) {
      password.push(getRandom(all))
    }

    return password.sort(() => Math.random() - 0.5).join("")
  }

  const submitForm = async () => {
    setLoading(true)

    const emailSchema = z.string().email().safeParse(email)
    if (!emailSchema.success) {
      setError(emailSchema.error.issues[0].message)
      setLoading(false)
      return
    }

    const result = await doesEmailExist(email)

    if (result?.found) {
      toast.error(
        "This email is already associated with an existing account. Please sign in."
      )
      setLoading(false)
      return
    }

    try {
      secureLocalStorage.clear()
      localStorage.clear()
      secureLocalStorage.setItem(
        "coachDetails",
        JSON.stringify(coachPublicProfileData)
      )
      setValidatingForm(true)
      if (!guestCheckout) {
        createAccount()
      }
    } catch (error) {
      console.error("Error creating account:", error)
      setError("Failed to create account. Please try again.")
      setLoading(false)
    }
  }

  const getGroupServiceEnrollment = async () => {
    try {
      const response = await ServiceRequests.checkGroupServiceEnrollment({
        stripe_product_id: service?.stripe_product_id,
      })
      const data = response
      const spotsLeft = data.spots_left
      setSpotsLeft(spotsLeft)
      return spotsLeft
    } catch (error) {
      console.error("Error fetching group service enrollment:", error)
      return null
    }
  }

  const handleVerificationAndEnrollment = async () => {
    await submitForm()
  }

  useEffect(() => {
    if (!email) {
      setEmailExists(false)
      return
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    debounceTimer.current = setTimeout(async () => {
      const result = await doesEmailExist(email)
      if (result?.found) {
        setEmailExists(true)
      } else {
        setEmailExists(false)
      }
    }, 500)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [email])

  const doesEmailExist = async (email: string) => {
    try {
      const data = await UserExists.verifyUserExists({
        attributes_to_verify: {
          email,
        },
      })

      return { found: data.user_found }
    } catch (error) {
      console.error(error)
    }
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setGuestEmail?.(value)
    setEmail(value)
    setIsTemp(2)

    const emailSchema = z.string().email().safeParse(value)
    if (!emailSchema.success) {
      return
    }
  }

  const handleNameChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: "first" | "last"
  ) => {
    const value = e.target.value
    if (field === "first") {
      setFirstName(value)
    } else {
      setLastName(value)
    }
  }

  const canSubmit =
    (firstName || "").trim().length > 0 &&
    (lastName || "").trim().length > 0 &&
    (email || "").trim().length > 0 &&
    !!isNotRobot

  useEffect(() => {
    if (String(coachPublicProfileData?.user_id) === user?.user_id) {
      setIsOwner(true)
    } else {
      setIsOwner(false)
    }
  }, [coachPublicProfileData, user])

  useEffect(() => {
    const tempPassword = generatePassword()
    setPassword(tempPassword)
  }, [setPassword])

  useEffect(() => {
    if (contactCreated) {
      setConnectionCompleted(true)
    }
  }, [contactCreated])

  useEffect(() => {
    if (service?.is_group_service) {
      getGroupServiceEnrollment()
    }
  }, [service])

  return (
    <div className="flex flex-col items-center w-full mb-4">
      {" "}
      <div className="gap-4 md:gap-20 mb-4 w-full p-0">
        <div className="flex flex-col gap-[12px] px-[20px] md:col-span-2 justify-center items-center w-full">
          <div className="w-full font-bold text-2xl">Enroll now!</div>
          <div className="flex flex-col w-[100%]">
            <Input
              label="Email"
              id="email"
              placeholder="Email Address"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
              style={{
                backgroundColor: errors.email ? "rgba(255, 0, 0, 0.05)" : "",
              }}
            />
            {errors.email && (
              <div className="flex items-center text-red-500 text-sm mt-1">
                <SvgWarning className="w-[18px] h-[18px] mr-1" />
                {errors.email}
              </div>
            )}
          </div>

          <div className="flex gap-4 w-[100%]">
            <div className="flex flex-col w-full">
              <Input
                label="First name"
                id="first-name"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => handleNameChange(e, "first")}
                disabled={loading}
                className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
              />
            </div>
            <div className="flex flex-col w-full">
              <Input
                label="Last name"
                id="last-name"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => handleNameChange(e, "last")}
                disabled={loading}
                className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
              />
            </div>
          </div>
          <div className="w-full mt-2">
            <GuestCheckoutSummary
              coachPublicProfileData={coachPublicProfileData}
              service={service}
              connectionCompleted={connectionCompleted}
              setLoading={setLoading}
              validatingForm={validatingForm}
              cardFieldErrors={cardFieldErrors}
              setCardFieldErrors={setCardFieldErrors}
              discountedOrderTotal={discountedOrderTotal}
              setDiscountedOrderTotal={setDiscountedOrderTotal}
            />
          </div>

          <div className="w-full">
            <div className="mb-4">
              <Recaptcha setIsNotRobot={setIsNotRobot} />
            </div>
            <Button
              onClick={handleVerificationAndEnrollment}
              disabled={!canSubmit || loading || fullEnrollment}
              className="w-full text-md ml-2"
            >
              {fullEnrollment ? (
                "No Spots Available"
              ) : loading ? (
                <Loader />
              ) : (
                "Continue"
              )}
            </Button>
          </div>
          {Number(service.service_details.billing.amount) !== 0 && (
            <div className="w-full text-center text-base text-muted-foreground -mt-2">
              <p className="flex justify-center gap-1 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <SvgLock2 className="h-4 w-4" />
                  Guaranteed
                </span>
                safe &amp; secure payments
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ServiceGuestCreation
